import React, { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Box, BoxProps } from '~Styles';
import { useStorage } from '~Components/Storage';
import { checkBrowserAllowClick } from '~utilities';

/** Same as the MainMenu slide */
const DURATION_MS = 450;
const EASING = 'cubic-bezier(0.23, 1, 0.32, 1)';

const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

/** Keyframes instead of transitions, so nothing has to stay mounted while closed */
const Backdrop = styled.div<{ closing: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 99;
  background: var(--colorDimmerBackground);
  animation: ${({ closing }) => (closing ? fadeOut : fadeIn)} ${DURATION_MS}ms ${EASING} forwards;
`;

const Panel = styled(Box)<{ closing: boolean; panelWidth: number }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 99;
  width: ${({ panelWidth }) => panelWidth}px;
  padding: 32px 16px;
  overflow: auto;
  background-color: var(--colorBlueGray100);
  box-shadow: 16px 0px 32px 0px rgba(0, 0, 0, 0.32);
  animation: ${({ closing }) => (closing ? slideOut : slideIn)} ${DURATION_MS}ms ${EASING} forwards;
`;

interface Props extends BoxProps {
  isShowing: boolean;
  hide: () => void;
  width?: number;
}

const SlideInPanel: FC<Props> = ({ isShowing, hide, width = 448, children, ...boxProps }) => {
  const [storage] = useStorage();
  const allowBrowserClick = checkBrowserAllowClick(storage.allowBrowserClick);

  const [isMounted, setIsMounted] = useState(isShowing);

  useEffect(() => {
    if (isShowing) {
      setIsMounted(true);
      return;
    }

    const id = setTimeout(() => setIsMounted(false), DURATION_MS);
    return () => clearTimeout(id);
  }, [isShowing]);

  if (!isShowing && !isMounted) {
    return null;
  }

  const closing = !isShowing;

  return createPortal(
    <>
      {/** Same close rules as Modal */}
      <Backdrop
        closing={closing}
        onTouchStart={hide}
        onClick={(e) => {
          if (!allowBrowserClick) {
            return;
          }

          e.stopPropagation();
          hide();
        }}
      />
      <Panel closing={closing} panelWidth={width} {...boxProps}>
        {children}
      </Panel>
    </>,
    document.body,
  );
};

export default SlideInPanel;
